import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'localization';
import { ChipAtom, EmptyStateAtom, MessageAtom } from 'design-system/my-lib';
import { StudioBuilder } from './builders';
import './StudioBuilderPlaceholder.scss';

export interface StudioBuilderPlaceholderProps {
  builder: StudioBuilder;
}

/**
 * The scaffolded builder body (EAINT-858 Phase 4, SC4) — one component parameterized by
 * `StudioBuilder`, mounted for every `status: 'scaffolded'` route in the registry. Proves the
 * three-region Studio frame (builder header, rail region, body) generalizes across builders
 * without fabricating registry content or mounting the space-shaped agent panel (see spec's S-02
 * correction note).
 */
const StudioBuilderPlaceholder: React.FC<StudioBuilderPlaceholderProps> = ({ builder }) => {
  const { t } = useTranslation();

  return (
    <div className="studio-builder-placeholder" data-testid={`studio-builder-scaffold-${builder.id}`}>
      <header className="studio-builder-placeholder__header">
        <i className={`${builder.icon} studio-builder-placeholder__icon`} aria-hidden="true" />
        <div className="studio-builder-placeholder__heading">
          <h1 className="studio-builder-placeholder__title">{t(builder.labelKey)}</h1>
          <p className="studio-builder-placeholder__description">{t(builder.descriptionKey)}</p>
        </div>
        <ChipAtom
          className="studio-builder-placeholder__chip"
          label={t('studio.home.scaffoldedChip')}
          data-testid={`studio-builder-placeholder-chip-${builder.id}`}
        />
      </header>

      {/* <aside>, not <div>: ARIA does not name generic elements, so an aria-label on a
          div is dropped by most screen readers and the rail would announce as unlabelled. */}
      <aside
        className="studio-builder-placeholder__rail"
        aria-label={t('studio.placeholder.railLabel')}
      >
        <MessageAtom severity="info" text={t('studio.placeholder.noRegistry')} />
      </aside>

      <div className="studio-builder-placeholder__body">
        <EmptyStateAtom
          icon="pi-hammer"
          title={t('studio.placeholder.notBuiltTitle')}
          description={t('studio.placeholder.notBuiltDescription')}
        />
        <Link to="/studio/space" className="studio-builder-placeholder__link">
          {t('studio.placeholder.spaceDesignerLinkLabel')}
        </Link>
      </div>
    </div>
  );
};

export default StudioBuilderPlaceholder;
